import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import {
  DEFAULT_NOTIFICATION_PREFERENCES,
  NOTIFICATION_PREFERENCE_LABELS,
  type NotificationPreferences,
  type NotificationPreferenceKey,
} from '@court-sync/shared';
import { Card, CardContent, CardHeader, Switch, Label } from '../../components/ui';
import { notificationPreferencesService } from '../../services/notificationPreferences';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { FEATURE_FLAG_KEYS } from '@court-sync/shared';

const PREFERENCE_KEYS: NotificationPreferenceKey[] = [
  'absence_reported',
  'makeup_lesson_picked',
  'low_enrolment',
  'refund_requested',
  'payment_failed',
  'new_booking_requested',
  'new_booking_confirmed',
  'eoi_submitted',
];

export default function SettingsNotificationsTab() {
  const [preferences, setPreferences] = useState<NotificationPreferences>(
    () => ({ ...DEFAULT_NOTIFICATION_PREFERENCES })
  );
  const [loading, setLoading] = useState(true);
  const { enabled: pushNotificationsEnabled } = useFeatureFlag(FEATURE_FLAG_KEYS.PUSH_NOTIFICATIONS);

  const fetchPreferences = useCallback(async () => {
    try {
      setLoading(true);
      const res = await notificationPreferencesService.getPreferences();
      if (res.success && res.data) {
        setPreferences(() => ({ ...DEFAULT_NOTIFICATION_PREFERENCES, ...res.data }));
      }
    } catch {
      toast.error('Failed to load notification preferences');
      setPreferences({ ...DEFAULT_NOTIFICATION_PREFERENCES });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  const handleToggle = async (
    key: NotificationPreferenceKey,
    channel: 'email' | 'inApp' | 'push',
    value: boolean
  ) => {
    const previous = { ...preferences[key] };
    setPreferences((prev) => ({
      ...prev,
      [key]: { ...prev[key], [channel]: value },
    }));

    try {
      const next = { ...preferences, [key]: { ...preferences[key], [channel]: value } };
      const res = await notificationPreferencesService.updatePreferences(next);
      if (!res.success) {
        throw new Error(res.error || 'Failed to save');
      }
      toast.success('Preferences saved');
    } catch {
      setPreferences((prev) => ({
        ...prev,
        [key]: previous,
      }));
      toast.error('Failed to save preferences');
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 w-48 bg-gray-200 dark:bg-gray-700 rounded" />
            <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded" />
            <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded" />
            <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <h2 className="text-lg font-semibold">Notification preferences</h2>
        <p className="text-sm text-muted-foreground">
          Choose how you receive notifications for each event.
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-1">
          <div className="grid grid-cols-12 gap-4 items-center border-b border-gray-200 dark:border-gray-700 pb-3 mb-2">
            <div className="col-span-5 sm:col-span-6 font-medium text-sm text-muted-foreground">
              Event
            </div>
            <div className="col-span-2 sm:col-span-2 text-center text-sm text-muted-foreground">
              Email
            </div>
            <div className="col-span-2 sm:col-span-2 text-center text-sm text-muted-foreground">
              In-app
            </div>
            {pushNotificationsEnabled && (
              <div className="col-span-2 sm:col-span-2 text-center text-sm text-muted-foreground">
                Push
              </div>
            )}
          </div>
          {PREFERENCE_KEYS.map((key) => (
            <div
              key={key}
              className="grid grid-cols-12 gap-4 items-center py-3 border-b border-gray-100 dark:border-gray-800 last:border-0"
            >
              <div className="col-span-5 sm:col-span-6">
                <Label className="text-sm font-normal">{NOTIFICATION_PREFERENCE_LABELS[key]}</Label>
              </div>
              <div className="col-span-2 sm:col-span-2 flex justify-center">
                <Switch
                  checked={preferences[key]?.email ?? true}
                  onCheckedChange={(checked) =>
                    handleToggle(key, 'email', checked === true)
                  }
                  aria-label={`Email for ${NOTIFICATION_PREFERENCE_LABELS[key]}`}
                />
              </div>
              <div className="col-span-2 sm:col-span-2 flex justify-center">
                <Switch
                  checked={preferences[key]?.inApp ?? true}
                  onCheckedChange={(checked) =>
                    handleToggle(key, 'inApp', checked === true)
                  }
                  aria-label={`In-app for ${NOTIFICATION_PREFERENCE_LABELS[key]}`}
                />
              </div>
              {pushNotificationsEnabled && (
                <div className="col-span-2 sm:col-span-2 flex justify-center">
                  <Switch
                    checked={preferences[key]?.push ?? false}
                    onCheckedChange={(checked) =>
                      handleToggle(key, 'push', checked === true)
                    }
                    aria-label={`Push for ${NOTIFICATION_PREFERENCE_LABELS[key]}`}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
